import type { components } from '@jervis/api-client';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, onCompanyIdRequest } from '../api.js';

type LoginResponse = components['schemas']['LoginResponse'];
type MfaChallengeResponse = components['schemas']['MfaChallengeResponse'];
type SessionInfo = components['schemas']['SessionInfo'];

export interface LoginInput {
  tenant: string;
  username: string;
  password: string;
}

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

/** Set by `login()` when the account has MFA enabled instead of a session (WP7.6, two-step login). */
export interface MfaPending {
  tenant: string;
  pendingToken: string;
  expiresAt: string;
}

function isMfaChallenge(data: LoginResponse | MfaChallengeResponse): data is MfaChallengeResponse {
  return 'mfaRequired' in data && data.mfaRequired === true;
}

/**
 * Authentication + "who am I" state (ADR-024): session cookie auth, CSRF token, entitled modules and
 * effective permissions drive the app shell and route guards. Not persisted — a reload re-derives it
 * from `GET /auth/session` (the httpOnly session cookie is what actually authenticates).
 */
export const useSessionStore = defineStore('session', () => {
  const status = ref<SessionStatus>('unknown');
  const user = ref<LoginResponse['user'] | null>(null);
  /** Always available after `refresh()`, even across a hard reload where the full `user` object is lost. */
  const username = ref<string | null>(null);
  /** Used by the Finance screens' SoD check (a creator must not be offered their own approval,
   * DECISIONS.md ADR-050 WP3.6) — the server is still the authority, this only drives the button. */
  const userId = ref<string | null>(null);
  const tenantId = ref<string | null>(null);
  const tenantSlug = ref<string | null>(null);
  const companyId = ref<string | null>(null);
  const companies = ref<string[] | null>(null);
  const permissions = ref<string[]>([]);
  const tenantPermissions = ref<string[]>([]);
  const modules = ref<string[]>([]);
  /** Set when `login()` returns an MFA challenge instead of a session (WP7.6). */
  const mfaPending = ref<MfaPending | null>(null);
  /** Set after a successful login/MFA-verify when a role requires MFA but none is enrolled yet. */
  const mfaSetupRequired = ref(false);

  const isAuthenticated = computed(() => status.value === 'authenticated');

  function hasModule(moduleId: string): boolean {
    return modules.value.includes(moduleId);
  }

  function hasPermission(key: string): boolean {
    return permissions.value.includes(key);
  }

  function hasTenantPermission(key: string): boolean {
    return tenantPermissions.value.includes(key);
  }

  function applySession(info: SessionInfo): void {
    username.value = info.username;
    userId.value = info.userId;
    tenantId.value = info.tenantId;
    companyId.value = info.companyId;
    companies.value = info.companies;
    permissions.value = info.permissions;
    tenantPermissions.value = info.tenantPermissions ?? [];
    modules.value = info.modules;
    status.value = 'authenticated';
  }

  function reset(): void {
    user.value = null;
    username.value = null;
    userId.value = null;
    tenantId.value = null;
    tenantSlug.value = null;
    companyId.value = null;
    companies.value = null;
    permissions.value = [];
    tenantPermissions.value = [];
    modules.value = [];
    mfaPending.value = null;
    mfaSetupRequired.value = false;
    status.value = 'anonymous';
  }

  /** Re-derives session state from the server (on boot, and after a 401). Never throws. */
  async function refresh(): Promise<boolean> {
    try {
      const { data, error } = await api.GET('/api/v1/auth/session');
      if (error || !data) {
        reset();
        return false;
      }
      applySession(data);
      return true;
    } catch {
      // API unreachable: treat as anonymous rather than surfacing a network error from the app shell.
      reset();
      return false;
    }
  }

  /** Resolves once fully signed in; leaves `mfaPending` set (and does not throw) when a second step is needed. */
  async function login(input: LoginInput): Promise<void> {
    const { data, error } = await api.POST('/api/v1/auth/login', { body: input });
    if (error || !data) {
      throw error;
    }
    if (isMfaChallenge(data)) {
      mfaPending.value = { tenant: input.tenant, pendingToken: data.pendingToken, expiresAt: data.expiresAt };
      return;
    }
    await applyLoginResponse(data);
  }

  /** Step 2 of the two-step login: submit a TOTP code or a recovery code against the pending challenge. */
  async function verifyMfa(input: { code?: string; recoveryCode?: string }): Promise<void> {
    if (!mfaPending.value) {
      throw new Error('No MFA challenge is pending');
    }
    const { data, error } = await api.POST('/api/v1/auth/mfa/verify', {
      body: {
        tenant: mfaPending.value.tenant,
        pendingToken: mfaPending.value.pendingToken,
        ...(input.code ? { code: input.code } : {}),
        ...(input.recoveryCode ? { recoveryCode: input.recoveryCode } : {}),
      },
    });
    if (error || !data) {
      throw error;
    }
    mfaPending.value = null;
    await applyLoginResponse(data);
  }

  function cancelMfaChallenge(): void {
    mfaPending.value = null;
  }

  async function applyLoginResponse(data: LoginResponse): Promise<void> {
    user.value = data.user;
    username.value = data.user.username;
    tenantSlug.value = data.tenant.slug;
    tenantId.value = data.tenant.id;
    mfaSetupRequired.value = data.mfaSetupRequired;
    status.value = 'authenticated';
    // Session cookie + CSRF cookie are now set; load the full permission/module set.
    await refresh();
  }

  async function logout(): Promise<void> {
    try {
      await api.POST('/api/v1/auth/logout');
    } catch {
      // Best-effort server revocation: always clear local session state even when offline.
    } finally {
      reset();
    }
  }

  // Every request carries the currently selected company as `x-company-id` (WP7.9b) — registered once
  // here (this `setup()` body runs a single time per Pinia instance) rather than from `main.ts`, so the
  // header stays in sync with `companyId` however it changes (login, the AppShell switcher, a reset).
  onCompanyIdRequest(() => companyId.value);

  return {
    status,
    user,
    username,
    userId,
    tenantId,
    tenantSlug,
    companyId,
    companies,
    permissions,
    tenantPermissions,
    modules,
    mfaPending,
    mfaSetupRequired,
    isAuthenticated,
    hasModule,
    hasPermission,
    hasTenantPermission,
    refresh,
    login,
    verifyMfa,
    cancelMfaChallenge,
    logout,
    reset,
  };
});
